import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { isInsideTelegram } from "@/lib/telegram";

/**
 * Deliberately not react-router: a Mini App has ~10 screens and no nested
 * layouts, and Telegram's own BackButton needs manual wiring regardless of
 * router choice, so a tiny hash-based router keeps the dependency surface
 * (and bundle size, relevant to the TTI budget in the TЗ) smaller.
 */

export type Route =
  | { name: "home" }
  | { name: "catalog"; category?: string; q?: string }
  | { name: "product"; slug: string }
  | { name: "cart" }
  | { name: "checkout" }
  | { name: "orderConfirmation"; orderId: string }
  | { name: "profile" }
  | { name: "favorites" }
  | { name: "orders" }
  | { name: "orderDetail"; orderId: string }
  | { name: "b2b" }
  | { name: "posts"; type?: "news" | "article" | "promo" }
  | { name: "post"; slug: string }
  | { name: "reviews"; slug?: string }
  | { name: "subscription"; id: string }
  | { name: "compare" }
  | { name: "wishlist"; code: string }
  | { name: "lead"; source?: string }
  | { name: "recipes" }
  | { name: "recipe"; slug: string }
  | { name: "gift"; code: string }
  | { name: "passport" }
  | { name: "addresses" }
  | { name: "events" }
  | { name: "ticket"; id: string }
  | { name: "faq"; category?: string }
  | { name: "taste" }
  | { name: "flavors" };

function routeToHash(route: Route): string {
  switch (route.name) {
    case "home":
      return "#/";
    case "catalog":
      {
        const qs = new URLSearchParams();
        if (route.category) qs.set("category", route.category);
        if (route.q) qs.set("q", route.q);
        const s = qs.toString();
        return s ? `#/catalog?${s}` : "#/catalog";
      }
    case "product":
      return `#/product/${route.slug}`;
    case "cart":
      return "#/cart";
    case "checkout":
      return "#/checkout";
    case "orderConfirmation":
      return `#/order-confirmation/${route.orderId}`;
    case "profile":
      return "#/profile";
    case "favorites":
      return "#/favorites";
    case "orders":
      return "#/orders";
    case "orderDetail":
      return `#/orders/${route.orderId}`;
    case "b2b":
      return "#/b2b";
    case "posts":
      return route.type ? `#/posts?type=${route.type}` : "#/posts";
    case "post":
      return `#/p/${route.slug}`;
    case "reviews":
      return route.slug ? `#/reviews/${route.slug}` : "#/reviews";
    case "subscription":
      return `#/subscription/${route.id}`;
    case "compare":
      return "#/compare";
    case "wishlist":
      return `#/wishlist/${route.code}`;
    case "lead":
      return route.source ? `#/lead?source=${encodeURIComponent(route.source)}` : "#/lead";
    case "recipes":
      return "#/recipes";
    case "recipe":
      return `#/recipes/${route.slug}`;
    case "gift":
      return `#/gift/${route.code}`;
    case "passport":
      return "#/passport";
    case "addresses":
      return "#/addresses";
    case "events":
      return "#/events";
    case "ticket":
      return `#/events/ticket/${route.id}`;
    case "faq":
      return route.category ? `#/faq?c=${route.category}` : "#/faq";
    case "taste":
      return "#/taste";
    case "flavors":
      return "#/flavors";
  }
}

function hashToRoute(hash: string): Route {
  const clean = hash.replace(/^#/, "") || "/";
  const [pathPart, query] = clean.split("?");
  const segments = pathPart.split("/").filter(Boolean);
  const params = new URLSearchParams(query ?? "");

  if (segments.length === 0) return { name: "home" };
  if (segments[0] === "catalog") return { name: "catalog", category: params.get("category") ?? undefined, q: params.get("q") ?? undefined };
  if (segments[0] === "product" && segments[1]) return { name: "product", slug: segments[1] };
  if (segments[0] === "cart") return { name: "cart" };
  if (segments[0] === "checkout") return { name: "checkout" };
  if (segments[0] === "order-confirmation" && segments[1]) return { name: "orderConfirmation", orderId: segments[1] };
  if (segments[0] === "profile") return { name: "profile" };
  if (segments[0] === "favorites") return { name: "favorites" };
  if (segments[0] === "orders" && segments[1]) return { name: "orderDetail", orderId: segments[1] };
  if (segments[0] === "orders") return { name: "orders" };
  if (segments[0] === "b2b") return { name: "b2b" };
  if (segments[0] === "posts") {
    const type = params.get("type");
    return { name: "posts", type: type === "news" || type === "article" || type === "promo" ? type : undefined };
  }
  if ((segments[0] === "p" || segments[0] === "page") && segments[1]) return { name: "post", slug: segments[1] };
  if (segments[0] === "reviews") return { name: "reviews", slug: segments[1] };
  if (segments[0] === "subscription" && segments[1]) return { name: "subscription", id: segments[1] };
  if (segments[0] === "compare") return { name: "compare" };
  if (segments[0] === "wishlist" && segments[1]) return { name: "wishlist", code: segments[1] };
  if (segments[0] === "lead") return { name: "lead", source: params.get("source") ?? undefined };
  if (segments[0] === "recipes" && segments[1]) return { name: "recipe", slug: segments[1] };
  if (segments[0] === "recipes") return { name: "recipes" };
  if (segments[0] === "gift" && segments[1]) return { name: "gift", code: segments[1] };
  if (segments[0] === "passport") return { name: "passport" };
  if (segments[0] === "addresses") return { name: "addresses" };
  if (segments[0] === "events" && segments[1] === "ticket" && segments[2]) return { name: "ticket", id: segments[2] };
  if (segments[0] === "events") return { name: "events" };
  if (segments[0] === "faq") return { name: "faq", category: params.get("c") ?? undefined };
  if (segments[0] === "taste") return { name: "taste" };
  if (segments[0] === "flavors") return { name: "flavors" };
  return { name: "home" };
}

/**
 * In a regular browser the site uses clean addresses (app.kms.coffee/product/…)
 * so links look good, can be shared with a preview and are indexed by search
 * engines. Inside Telegram the Mini App keeps the hash router exactly as before.
 * Old "#/…" links keep working everywhere: they are converted on arrival.
 */
const PATH_MODE = typeof window !== "undefined" && !isInsideTelegram();

/** Clean address for a screen (browser only). */
export function routeToPath(route: Route): string {
  switch (route.name) {
    case "home":
      return "/";
    case "catalog": {
      const qs = route.q ? `?q=${encodeURIComponent(route.q)}` : "";
      return `${route.category ? `/catalog/${encodeURIComponent(route.category)}` : "/catalog"}${qs}`;
    }
    case "post":
      return route.slug === "about" ? "/about" : `/blog/${route.slug}`;
    case "posts":
      return route.type ? `/blog?type=${route.type}` : "/blog";
    case "lead":
      return route.source ? `/b2b?source=${encodeURIComponent(route.source)}` : "/b2b";
    case "b2b":
      return "/b2b";
    case "profile":
      return "/account";
    case "orders":
      return "/account/orders";
    case "orderDetail":
      return `/account/orders/${route.orderId}`;
    case "favorites":
      return "/account/favorites";
    case "addresses":
      return "/account/addresses";
    case "orderConfirmation":
      return `/order/${route.orderId}`;
    default:
      return routeToHash(route).replace(/^#/, "");
  }
}

/** Reads a clean address back into a screen. Unknown paths → home. */
export function pathToRoute(pathname: string, search: string): Route {
  const seg = pathname.split("/").filter(Boolean).map((x) => {
    try {
      return decodeURIComponent(x);
    } catch {
      return x;
    }
  });
  const params = new URLSearchParams(search);
  const q = params.get("q") ?? undefined;
  switch (seg[0]) {
    case undefined:
      return { name: "home" };
    case "catalog":
      return { name: "catalog", category: seg[1] ?? params.get("category") ?? undefined, q };
    case "blog":
      return seg[1] ? { name: "post", slug: seg[1] } : hashToRoute(`#/posts${search}`);
    case "about":
      return { name: "post", slug: "about" };
    case "b2b":
    case "lead":
      return { name: "lead", source: params.get("source") ?? undefined };
    case "account":
      if (seg[1] === "orders") return seg[2] ? { name: "orderDetail", orderId: seg[2] } : { name: "orders" };
      if (seg[1] === "favorites") return { name: "favorites" };
      if (seg[1] === "addresses") return { name: "addresses" };
      return { name: "profile" };
    case "order":
      return seg[1] ? { name: "orderConfirmation", orderId: seg[1] } : { name: "home" };
    default:
      return hashToRoute(`#${pathname}${search}`);
  }
}

/** Current screen from the address bar (a "#/…" link wins — it is an old-style link). */
function readLocation(): Route {
  const { hash, pathname, search } = window.location;
  if (!PATH_MODE) return hashToRoute(hash);
  if (hash.startsWith("#/")) return hashToRoute(hash);
  return pathToRoute(pathname, search);
}

/** Browser: turn an old "#/…" link into the clean address without a page reload. */
function normaliseLocation(route: Route) {
  if (!PATH_MODE) return;
  const { hash, pathname, search } = window.location;
  const clean = routeToPath(route);
  if (hash.startsWith("#/") || `${pathname}${search}` !== clean) {
    try {
      window.history.replaceState(window.history.state, "", clean);
    } catch {
      /* ignore */
    }
  }
}

interface RouterContextValue {
  route: Route;
  navigate: (route: Route) => void;
  goBack: () => void;
}

const RouterContext = createContext<RouterContextValue | null>(null);

export function RouterProvider({ children }: { children: React.ReactNode }) {
  const [route, setRoute] = useState<Route>(readLocation);

  useEffect(() => {
    normaliseLocation(route);
    const sync = () => {
      const next = readLocation();
      normaliseLocation(next);
      setRoute(next);
    };
    window.addEventListener("hashchange", sync);
    if (PATH_MODE) window.addEventListener("popstate", sync);
    return () => {
      window.removeEventListener("hashchange", sync);
      window.removeEventListener("popstate", sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<RouterContextValue>(
    () => ({
      route,
      navigate: (next) => {
        if (!PATH_MODE) {
          window.location.hash = routeToHash(next);
          return;
        }
        const path = routeToPath(next);
        if (`${window.location.pathname}${window.location.search}` !== path || window.location.hash) {
          window.history.pushState(null, "", path);
        }
        setRoute(next);
      },
      goBack: () => {
        // Opened straight on an inner page (from a shared link): "back" goes to the home page.
        if (PATH_MODE && window.history.length <= 1) {
          window.history.replaceState(null, "", "/");
          setRoute({ name: "home" });
          return;
        }
        window.history.back();
      },
    }),
    [route],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterContextValue {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error("useRouter must be used within RouterProvider");
  return ctx;
}
